'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Award, ChevronRight, ExternalLink, Printer, Shield } from 'lucide-react';
import { stages } from '@/app/components/PropertyTaxCycleDiagram';
import { wbSteps } from '@/app/gis-property-hub/components/WorldBankGISFrameworkPanel';
import AppLayout from '@/components/AppLayout';

const statusConfig = {
  implemented: { label: 'Implemented', color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
  partial: { label: 'Partial', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  planned: { label: 'Planned', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

export default function SystemMethodologyClient() {
  const [activeView, setActiveView] = useState<'side-by-side' | 'alignment'>('side-by-side');

  const avgWBScore = Math.round(wbSteps.reduce((a, b) => a + b.complianceScore, 0) / wbSteps.length);
  const implementedCount = wbSteps.filter(s => s.status === 'implemented').length;

  return (
    <AppLayout>
    <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/5"
            style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
          >
            <ArrowLeft size={13} />
            Dashboard
          </Link>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Shield size={14} style={{ color: 'var(--accent)' }} />
              <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>
                Methodology
              </span>
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--dark-text)' }}>
              System Methodology
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>
              LRA Property Tax Cycle × World Bank GIS Framework — International Standards Alignment
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/5"
            style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
          >
            <Printer size={13} />
            Print
          </button>
        </div>
      </div>

      {/* Proof bar */}
      <div
        className="rounded-xl p-5 mb-6"
        style={{ background: 'linear-gradient(135deg, rgba(212,160,23,0.08) 0%, rgba(59,130,246,0.06) 100%)', border: '1px solid rgba(212,160,23,0.2)' }}
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'LRA Cycle Stages', value: '9', sub: 'End-to-end coverage', color: 'var(--accent)' },
            { label: 'World Bank Standards', value: `${implementedCount}/${wbSteps.length}`, sub: 'Fully implemented', color: '#10b981' },
            { label: 'WB Compliance Score', value: `${avgWBScore}%`, sub: 'Overall readiness', color: '#60a5fa' },
            { label: 'Cross-Alignments', value: '18', sub: 'Cycle ↔ WB links', color: '#a78bfa' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-3xl font-bold font-mono" style={{ color: stat.color }}>{stat.value}</p>
              <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--dark-text)' }}>{stat.label}</p>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{stat.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2 mb-5">
        {([
          { id: 'side-by-side', label: 'Side by Side' },
          { id: 'alignment', label: 'Alignment Map' },
        ] as const).map((v) => (
          <button
            key={v.id}
            onClick={() => setActiveView(v.id)}
            className="px-4 py-2 rounded-lg text-xs font-semibold transition-all"
            style={{
              backgroundColor: activeView === v.id ? 'rgba(212,160,23,0.15)' : 'var(--dark-card)',
              color: activeView === v.id ? 'var(--accent)' : 'var(--dark-muted)',
              border: activeView === v.id ? '1px solid rgba(212,160,23,0.3)' : '1px solid var(--dark-border)',
            }}
          >
            {v.label}
          </button>
        ))}
      </div>

      {activeView === 'side-by-side' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* LRA Property Tax Cycle */}
          <div
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: 'var(--dark-card)', border: '1px solid rgba(212,160,23,0.25)' }}
          >
            <div
              className="px-5 py-4 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg, rgba(212,160,23,0.12) 0%, rgba(212,160,23,0.04) 100%)', borderBottom: '1px solid rgba(212,160,23,0.2)' }}
            >
              <div>
                <h2 className="text-base font-bold" style={{ color: 'var(--accent)' }}>
                  LRA Property Tax Cycle
                </h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                  9 stages · Built for Liberia's property tax system
                </p>
              </div>
              <Link
                href="/property-tax-cycle"
                className="flex items-center gap-1 text-xs font-medium transition-all hover:opacity-80"
                style={{ color: 'var(--accent)' }}
              >
                Full View <ExternalLink size={11} />
              </Link>
            </div>
            <div className="p-4 space-y-2">
              {stages.map((stage) => (
                <div
                  key={stage.id}
                  className="flex items-start gap-3 p-3 rounded-lg transition-all hover:bg-white/3"
                  style={{ border: '1px solid var(--dark-border)' }}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: `${stage.color}15`, color: stage.color, border: `1px solid ${stage.color}30` }}
                  >
                    {stage.id}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>
                        {stage.title}
                      </p>
                      {/* WB alignment badge */}
                      {wbSteps.some(w => w.alignedCycleStages.includes(stage.id)) && (
                        <span
                          className="text-xs px-1.5 py-0.5 rounded font-bold flex-shrink-0"
                          style={{ backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa' }}
                        >
                          WB ✓
                        </span>
                      )}
                    </div>
                    <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--dark-muted)' }}>
                      → {stage.moduleName}
                    </p>
                  </div>
                  <Link href={stage.moduleLink}>
                    <ChevronRight size={13} style={{ color: 'var(--dark-muted)' }} />
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* World Bank GIS Framework */}
          <div
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: 'var(--dark-card)', border: '1px solid rgba(59,130,246,0.25)' }}
          >
            <div
              className="px-5 py-4 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0.03) 100%)', borderBottom: '1px solid rgba(59,130,246,0.2)' }}
            >
              <div>
                <h2 className="text-base font-bold" style={{ color: '#60a5fa' }}>
                  World Bank GIS Framework
                </h2>
                <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                  {wbSteps.length} standards · International land administration
                </p>
              </div>
              <Link
                href="/gis-property-hub"
                className="flex items-center gap-1 text-xs font-medium transition-all hover:opacity-80"
                style={{ color: '#60a5fa' }}
              >
                GIS Hub <ExternalLink size={11} />
              </Link>
            </div>
            <div className="p-4 space-y-2">
              {wbSteps.map((step) => {
                const cfg = statusConfig[step.status];
                return (
                  <div
                    key={step.id}
                    className="flex items-start gap-3 p-3 rounded-lg transition-all hover:bg-white/3"
                    style={{ border: '1px solid var(--dark-border)' }}
                  >
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: cfg.bg, color: cfg.color }}
                    >
                      {step.id}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>
                          {step.step}
                        </p>
                        {step.alignedCycleStages.length > 0 && (
                          <div className="flex gap-1">
                            {step.alignedCycleStages.map(s => (
                              <span
                                key={s}
                                className="text-xs px-1 py-0.5 rounded font-bold"
                                style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}
                              >
                                C{s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-surface)' }}>
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${step.complianceScore}%`, backgroundColor: cfg.color }}
                          />
                        </div>
                        <span className="text-xs font-bold flex-shrink-0" style={{ color: cfg.color }}>
                          {step.complianceScore}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeView === 'alignment' && (
        <div
          className="rounded-xl overflow-hidden"
          style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
        >
          <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--dark-border)' }}>
            <h3 className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>
              Cross-Framework Alignment Map
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
              Each World Bank standard mapped to its corresponding LRA Property Tax Cycle stage(s)
            </p>
          </div>
          <div className="p-5 space-y-3">
            {wbSteps.map((wbStep) => {
              const cfg = statusConfig[wbStep.status];
              const linkedStages = stages.filter(s => wbStep.alignedCycleStages.includes(s.id));
              return (
                <div
                  key={wbStep.id}
                  className="rounded-xl p-4"
                  style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: cfg.bg, color: cfg.color }}
                    >
                      WB{wbStep.id}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
                          {wbStep.step}
                        </h4>
                        <span
                          className="text-xs px-1.5 py-0.5 rounded font-medium"
                          style={{ backgroundColor: cfg.bg, color: cfg.color }}
                        >
                          {cfg.label} · {wbStep.complianceScore}%
                        </span>
                      </div>
                      <p className="text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>
                        {wbStep.lraNexusFeature}
                      </p>
                    </div>
                  </div>

                  {linkedStages.length > 0 ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Aligned to:</span>
                      {linkedStages.map((stage) => (
                        <Link
                          key={stage.id}
                          href="/property-tax-cycle"
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-85"
                          style={{
                            backgroundColor: `${stage.color}15`,
                            color: stage.color,
                            border: `1px solid ${stage.color}30`,
                          }}
                        >
                          <span>Stage {stage.id}</span>
                          <span style={{ color: 'rgba(255,255,255,0.5)' }}>·</span>
                          <span>{stage.shortTitle}</span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
                      style={{ backgroundColor: 'rgba(107,114,128,0.1)', color: '#6b7280' }}
                    >
                      <Award size={11} />
                      Institutional / Cross-cutting standard
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Link
          href="/property-tax-cycle"
          className="flex items-center justify-between p-5 rounded-xl transition-all hover:opacity-85"
          style={{ background: 'linear-gradient(135deg, rgba(212,160,23,0.12) 0%, rgba(212,160,23,0.05) 100%)', border: '1px solid rgba(212,160,23,0.25)' }}
        >
          <div>
            <p className="text-sm font-bold" style={{ color: 'var(--accent)' }}>LRA Property Tax Cycle</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Interactive 9-stage diagram with live KPIs</p>
          </div>
          <ChevronRight size={18} style={{ color: 'var(--accent)' }} />
        </Link>
        <Link
          href="/gis-property-hub"
          className="flex items-center justify-between p-5 rounded-xl transition-all hover:opacity-85"
          style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0.04) 100%)', border: '1px solid rgba(59,130,246,0.25)' }}
        >
          <div>
            <p className="text-sm font-bold" style={{ color: '#60a5fa' }}>World Bank GIS Framework</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Full readiness matrix in GIS Property Hub</p>
          </div>
          <ChevronRight size={18} style={{ color: '#60a5fa' }} />
        </Link>
      </div>
    </div>
    </AppLayout>
  );
}
